import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs, doc, deleteDoc, setDoc } from 'firebase/firestore';
import Sidebar from '../Shared/Sidebar';
import Icon from '../Shared/Icons';

// ── Chess Accounts — usafi wa akaunti ──────────────────────────────────
// Some players registered the same nickname twice before unique names
// were enforced. This page finds those duplicates, keeps the strongest
// account (most games, then highest rating, then most recent), and lets
// an admin delete the rest. It also backfills `nameLower`, the field the
// app now uses to block duplicate nicknames at sign-up.

function score(p) {
  return [(p.gamesPlayed || 0), (p.rating || 0), (p.updatedAt?.seconds || 0)];
}
function better(a, b) {
  const sa = score(a), sb = score(b);
  for (let i = 0; i < sa.length; i++) {
    if (sa[i] !== sb[i]) return sa[i] > sb[i] ? a : b;
  }
  return a;
}

export default function ChessAccounts() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function load() {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'chessPlayers'));
      setPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
      setMessage('Imeshindikana kupakia akaunti. Angalia Firestore rules.');
    }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  // Group by lowercase name — that is what counts as "the same person"
  const groups = {};
  players.forEach(p => {
    const key = (p.nameLower || p.name || '').trim().toLowerCase();
    if (!key) return;
    (groups[key] = groups[key] || []).push(p);
  });
  const duplicateGroups = Object.entries(groups)
    .filter(([, list]) => list.length > 1)
    .sort((a, b) => b[1].length - a[1].length);

  const missingLower = players.filter(p => !p.nameLower && p.name);

  async function handleBackfill() {
    if (busy || missingLower.length === 0) return;
    setBusy(true);
    setMessage('');
    let done = 0;
    for (const p of missingLower) {
      try {
        await setDoc(doc(db, 'chessPlayers', p.id), {
          nameLower: (p.name || '').toLowerCase(),
        }, { merge: true });
        done++;
      } catch (e) { console.error(e); }
    }
    setBusy(false);
    setMessage(`Imekamilika: akaunti ${done} zimewekwa alama ya jina.`);
    load();
  }

  async function handleMerge(key, list) {
    const keeper = list.reduce(better);
    const removing = list.filter(p => p.id !== keeper.id);
    const ok = window.confirm(
      `Jina "${keeper.name}":\n\n` +
      `ITABAKI: rating ${keeper.rating || 0}, michezo ${keeper.gamesPlayed || 0}\n` +
      `ZITAFUTWA: akaunti ${removing.length}\n\n` +
      `Kitendo hiki hakiwezi kutenguliwa. Endelea?`
    );
    if (!ok) return;
    setBusy(true);
    setMessage('');
    let deleted = 0;
    for (const p of removing) {
      try {
        await deleteDoc(doc(db, 'chessPlayers', p.id));
        deleted++;
      } catch (e) {
        console.error(e);
        setMessage('Baadhi hazikufutika — hakikisha umeingia kama Admin.');
      }
    }
    // make sure the survivor carries the uniqueness marker
    try {
      await setDoc(doc(db, 'chessPlayers', keeper.id), {
        nameLower: (keeper.name || '').toLowerCase(),
      }, { merge: true });
    } catch (e) { console.error(e); }
    setBusy(false);
    if (deleted > 0) setMessage(`Akaunti ${deleted} zimefutwa. "${keeper.name}" imebaki moja.`);
    load();
  }

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main} className="main-content">
        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>Akaunti za Chess</div>
            <div style={styles.pageSub}>
              Tafuta majina yaliyosajiliwa zaidi ya mara moja, kisha baki na akaunti moja tu kwa kila mchezaji.
            </div>
          </div>
          <button style={styles.refreshBtn} onClick={load} disabled={busy || loading}>
            Pakia Upya
          </button>
        </div>

        {message && <div style={styles.message}>{message}</div>}

        <div style={styles.summaryRow}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryNum}>{players.length}</div>
            <div style={styles.summaryLabel}>Akaunti zote</div>
          </div>
          <div style={{ ...styles.summaryCard, borderColor: duplicateGroups.length ? '#FFCDD2' : '#BBD8C4' }}>
            <div style={{ ...styles.summaryNum, color: duplicateGroups.length ? '#C62828' : '#14452F' }}>
              {duplicateGroups.length}
            </div>
            <div style={styles.summaryLabel}>Majina yaliyorudiwa</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryNum}>{missingLower.length}</div>
            <div style={styles.summaryLabel}>Hazina alama ya jina</div>
          </div>
        </div>

        {missingLower.length > 0 && (
          <div style={styles.backfillBox}>
            <div style={{ flex: 1, minWidth: '220px' }}>
              <div style={styles.backfillTitle}>Weka alama ya jina (nameLower)</div>
              <div style={styles.backfillText}>
                Akaunti {missingLower.length} zilisajiliwa kabla ya sheria ya majina ya kipekee.
                Bonyeza hapa ili zilindwe pia — baada ya hapo hakuna atakayeweza kuchukua majina yao.
              </div>
            </div>
            <button style={styles.primaryBtn} onClick={handleBackfill} disabled={busy}>
              {busy ? 'Inafanya kazi…' : 'Weka Alama'}
            </button>
          </div>
        )}

        {loading ? (
          <div style={styles.empty}>Inapakia...</div>
        ) : duplicateGroups.length === 0 ? (
          <div style={styles.cleanBox}>
            <Icon name="check" size={26} color="#14452F" />
            <div style={styles.cleanText}>Hakuna jina lililorudiwa. Kila mchezaji ana akaunti moja.</div>
          </div>
        ) : (
          <div style={styles.list}>
            {duplicateGroups.map(([key, list]) => {
              const keeper = list.reduce(better);
              return (
                <div key={key} style={styles.card}>
                  <div style={styles.cardHead}>
                    <div>
                      <div style={styles.dupName}>{list[0].name}</div>
                      <div style={styles.dupMeta}>Akaunti {list.length} zenye jina hili</div>
                    </div>
                    <button style={styles.mergeBtn} onClick={() => handleMerge(key, list)} disabled={busy}>
                      Baki na Moja
                    </button>
                  </div>
                  <div style={styles.accountRows}>
                    {list.map(p => {
                      const keep = p.id === keeper.id;
                      return (
                        <div key={p.id} style={{ ...styles.accountRow, ...(keep ? styles.accountRowKeep : {}) }}>
                          <span style={{ ...styles.tag, background: keep ? '#EAF3EC' : '#FFEBEE', color: keep ? '#14452F' : '#C62828' }}>
                            {keep ? 'ITABAKI' : 'ITAFUTWA'}
                          </span>
                          <span style={styles.accStat}>Rating: <strong>{p.rating || 0}</strong></span>
                          <span style={styles.accStat}>Michezo: <strong>{p.gamesPlayed || 0}</strong></span>
                          <span style={styles.accId}>{p.id.slice(0, 8)}…</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#F6F4EE' },
  main: { flex: 1, padding: '26px', overflowY: 'auto' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap', marginBottom: '18px' },
  pageTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '25px', color: '#14452F', fontWeight: '700' },
  pageSub: { fontSize: '12.5px', color: '#6B7280', marginTop: '3px', maxWidth: '520px' },
  refreshBtn: { background: 'transparent', border: '1.5px solid #14452F', color: '#14452F', padding: '10px 18px', borderRadius: '10px', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  message: { background: '#EAF3EC', border: '1px solid #BBD8C4', color: '#14452F', borderRadius: '12px', padding: '12px 16px', fontSize: '13px', fontWeight: '600', marginBottom: '16px' },
  summaryRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '18px' },
  summaryCard: { background: '#fff', border: '1.5px solid rgba(0,0,0,0.07)', borderRadius: '14px', padding: '16px 18px' },
  summaryNum: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '27px', fontWeight: '700', color: '#14452F' },
  summaryLabel: { fontSize: '12px', color: '#6B7280', marginTop: '2px' },
  backfillBox: { display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', background: '#FFF8E9', border: '1.5px solid #F0DEAE', borderRadius: '14px', padding: '16px 20px', marginBottom: '18px' },
  backfillTitle: { fontSize: '14px', fontWeight: '800', color: '#7A5B00' },
  backfillText: { fontSize: '12.5px', color: '#6B7280', marginTop: '3px', lineHeight: 1.6 },
  primaryBtn: { background: 'linear-gradient(135deg, #14452F, #1F6B45)', color: '#fff', border: 'none', padding: '12px 22px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 },
  cleanBox: { display: 'flex', alignItems: 'center', gap: '12px', background: '#fff', border: '1px solid #BBD8C4', borderRadius: '14px', padding: '24px' },
  cleanText: { fontSize: '13.5px', color: '#14452F', fontWeight: '600' },
  list: { display: 'flex', flexDirection: 'column', gap: '12px', paddingBottom: '30px' },
  card: { background: '#fff', borderRadius: '14px', border: '1px solid rgba(0,0,0,0.06)', padding: '16px 18px' },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  dupName: { fontSize: '15px', fontWeight: '800', color: '#1C1C1E' },
  dupMeta: { fontSize: '11.5px', color: '#9CA3AF', marginTop: '2px' },
  mergeBtn: { background: '#C62828', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '10px', fontSize: '12.5px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  accountRows: { display: 'flex', flexDirection: 'column', gap: '7px' },
  accountRow: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', background: '#FAF9F5', borderRadius: '10px', padding: '10px 14px' },
  accountRowKeep: { background: '#F2F7F3', border: '1px solid #D5E6D9' },
  tag: { fontSize: '10.5px', fontWeight: '800', padding: '4px 10px', borderRadius: '20px' },
  accStat: { fontSize: '12px', color: '#4B5563' },
  accId: { fontSize: '11px', color: '#C4C4C4', marginLeft: 'auto', fontFamily: 'monospace' },
  empty: { textAlign: 'center', color: '#9CA3AF', fontSize: '13px', padding: '40px 0' },
};
